import { FlowArrow } from '@phosphor-icons/react';
import {
  canConnect,
  fittingSource,
  getNodeDefinition,
  portKey,
} from '@reelplicate/flows';
import { EmptyState, toast } from '@reelplicate/ui';
import {
  Background,
  BackgroundVariant,
  type Connection,
  type EdgeChange,
  type FinalConnectionState,
  MiniMap,
  type NodeChange,
  ReactFlow,
  useReactFlow,
} from '@xyflow/react';
import { type DragEvent, useCallback, useMemo, useState } from 'react';

import { DataEdge, type FlowCanvasEdge } from './data-edge';
import { useEditor, useEditorStore } from './editor-store';
import { type FlowCanvasNode, FlowNode } from './flow-node';
import { useRun } from './run/run-state';
import { useAnalysis } from './use-analysis';
import { ZoomControls } from './zoom-controls';

/** The drag payload a library row carries. */
export const NODE_DRAG_TYPE = 'application/x-reelplicate-node';

const nodeTypes = { flow: FlowNode };

/**
 * Figma Minimap (69:158) colours. The minimap paints SVG fill attributes,
 * where CSS variables don't resolve, so these are the tokens' values:
 * border/strong, brand/strong, status/danger/border, border/focus.
 */
const MINIMAP = {
  node: '#919283',
  selected: '#505b2c',
  error: '#fda297',
  viewport: '#67752b',
};
const edgeTypes = { data: DataEdge };

export function FlowCanvas() {
  const store = useEditorStore();
  const graph = useEditor((state) => state.graph);
  const selectedNodeIds = useEditor((state) => state.selectedNodeIds);
  const selectedEdgeIds = useEditor((state) => state.selectedEdgeIds);
  const { nodeErrors, nodeWarnings, edgeErrors, types, estimate } = useAnalysis();
  const { screenToFlowPosition } = useReactFlow();
  const { run } = useRun();
  // Measured sizes belong to the canvas, not the saved graph, but React Flow
  // needs them back on the nodes (the minimap and edge routing read them).
  const [measured, setMeasured] = useState<
    ReadonlyMap<string, { width: number; height: number }>
  >(() => new Map());

  const remove = useCallback(
    (nodeId: string) => store.getState().remove([nodeId]),
    [store],
  );

  const nodes = useMemo<FlowCanvasNode[]>(() => {
    const connected = new Map<string, Set<string>>();
    const mark = (nodeId: string, key: string) => {
      if (!connected.has(nodeId)) connected.set(nodeId, new Set());
      connected.get(nodeId)!.add(key);
    };
    for (const edge of graph.edges) {
      mark(edge.source.nodeId, `out:${edge.source.port}`);
      mark(edge.target.nodeId, `in:${edge.target.port}`);
    }
    const selected = new Set(selectedNodeIds);
    return graph.nodes.flatMap((node) => {
      const definition = getNodeDefinition(node.type, node.typeVersion);
      if (!definition) return [];
      return [
        {
          id: node.id,
          type: 'flow' as const,
          position: node.position,
          measured: measured.get(node.id),
          selected: selected.has(node.id),
          data: {
            node,
            definition,
            error: nodeErrors.get(node.id),
            warning: nodeWarnings.get(node.id),
            connected: connected.get(node.id) ?? new Set<string>(),
            onDelete: remove,
            run: run?.nodes.find((n) => n.nodeId === node.id),
            held: run?.held.filter((item) => item.nodeId === node.id) ?? [],
          },
        },
      ];
    });
  }, [graph, selectedNodeIds, nodeErrors, nodeWarnings, remove, measured, run]);

  const edges = useMemo<FlowCanvasEdge[]>(() => {
    const selected = new Set(selectedEdgeIds);
    return graph.edges.map((edge) => ({
      id: edge.id,
      type: 'data' as const,
      source: edge.source.nodeId,
      sourceHandle: edge.source.port,
      target: edge.target.nodeId,
      targetHandle: edge.target.port,
      selected: selected.has(edge.id),
      data: {
        type: types.get(portKey(edge.source.nodeId, edge.source.port)),
        // What actually went along it in the run shown, else the estimate.
        count:
          run?.edges.find((e) => e.edgeId === edge.id)?.count ?? estimate.edges[edge.id],
        error: edgeErrors.get(edge.id),
      },
    }));
  }, [graph, selectedEdgeIds, types, estimate, edgeErrors, run]);

  const onNodesChange = useCallback(
    (changes: NodeChange<FlowCanvasNode>[]) => {
      const state = store.getState();
      const positions = new Map<string, { x: number; y: number }>();
      let selection: Set<string> | null = null;
      const sizes: [string, { width: number; height: number }][] = [];
      for (const change of changes) {
        if (change.type === 'dimensions' && change.dimensions)
          sizes.push([change.id, change.dimensions]);
        if (change.type === 'position' && change.position)
          positions.set(change.id, change.position);
        if (change.type === 'select') {
          selection ??= new Set(state.selectedNodeIds);
          if (change.selected) selection.add(change.id);
          else selection.delete(change.id);
        }
      }
      if (sizes.length) setMeasured((current) => new Map([...current, ...sizes]));
      if (positions.size) state.move(positions);
      if (selection) state.select([...selection], state.selectedEdgeIds);
    },
    [store],
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange<FlowCanvasEdge>[]) => {
      const state = store.getState();
      let selection: Set<string> | null = null;
      for (const change of changes) {
        if (change.type === 'select') {
          selection ??= new Set(state.selectedEdgeIds);
          if (change.selected) selection.add(change.id);
          else selection.delete(change.id);
        }
      }
      if (selection) state.select(state.selectedNodeIds, [...selection]);
    },
    [store],
  );

  const onConnect = useCallback(
    (connection: Connection) => {
      const result = store
        .getState()
        .connect(
          { nodeId: connection.source, port: connection.sourceHandle ?? 'out' },
          { nodeId: connection.target, port: connection.targetHandle ?? 'in' },
        );
      if (!result.ok) toast.warning(result.reason);
      else if (result.guardrailId) {
        toast.warning('Added Brand guardrails before Schedule & publish', {
          description: 'Everything that gets published passes your brand rules first.',
          action: { label: 'Undo', onClick: () => store.getState().undo() },
        });
      }
    },
    [store],
  );

  const isValidConnection = useCallback(
    (connection: Connection | FlowCanvasEdge) =>
      fittingSource(
        store.getState().graph,
        { nodeId: connection.source, port: connection.sourceHandle ?? 'out' },
        { nodeId: connection.target, port: connection.targetHandle ?? 'in' },
      ) !== undefined,
    [store],
  );

  /** Says why a drop on a port was refused, instead of silently snapping back. */
  const onConnectEnd = useCallback(
    (_event: MouseEvent | TouchEvent, state: FinalConnectionState) => {
      if (state.isValid !== false || !state.fromHandle || !state.toHandle) return;
      const [from, to] =
        state.fromHandle.type === 'source'
          ? [state.fromHandle, state.toHandle]
          : [state.toHandle, state.fromHandle];
      const result = canConnect(
        store.getState().graph,
        { nodeId: from.nodeId, port: from.id ?? 'out' },
        { nodeId: to.nodeId, port: to.id ?? 'in' },
      );
      if (!result.ok) toast.warning(result.reason);
    },
    [store],
  );

  const onDrop = useCallback(
    (event: DragEvent) => {
      const type = event.dataTransfer.getData(NODE_DRAG_TYPE);
      if (!type) return;
      event.preventDefault();
      const point = screenToFlowPosition({ x: event.clientX, y: event.clientY });
      store.getState().addNode(type, { x: point.x - 124, y: point.y - 30 });
    },
    [store, screenToFlowPosition],
  );

  return (
    <div className="bg-bg-canvas relative min-w-0 flex-1" data-slot="flow-canvas">
      <ReactFlow<FlowCanvasNode, FlowCanvasEdge>
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeDragStart={() => store.getState().beginMove()}
        onConnect={onConnect}
        onConnectEnd={onConnectEnd}
        isValidConnection={isValidConnection}
        onDelete={({ nodes: gone, edges: cut }) =>
          store.getState().remove(
            gone.map((node) => node.id),
            cut.map((edge) => edge.id),
          )
        }
        onDragOver={(event) => {
          if (event.dataTransfer.types.includes(NODE_DRAG_TYPE)) {
            event.preventDefault();
            event.dataTransfer.dropEffect = 'copy';
          }
        }}
        onDrop={onDrop}
        deleteKeyCode={['Backspace', 'Delete']}
        fitView
        fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
        minZoom={0.2}
        maxZoom={2}
        attributionPosition="top-right"
        aria-label="Flow canvas"
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={20}
          size={1.5}
          color="var(--color-border-default)"
        />
        <ZoomControls />
        <MiniMap
          pannable
          zoomable
          position="bottom-right"
          ariaLabel="Minimap"
          className="!bg-bg-surface !border-border-subtle !m-4 !h-28 !w-44 overflow-hidden !rounded-md border !shadow-sm"
          nodeBorderRadius={3}
          nodeColor={(node) =>
            nodeErrors.has(node.id)
              ? MINIMAP.error
              : node.selected
                ? MINIMAP.selected
                : MINIMAP.node
          }
          maskColor="rgb(248 249 239 / 0.6)"
          maskStrokeColor={MINIMAP.viewport}
          maskStrokeWidth={1.5}
        />
      </ReactFlow>
      {graph.nodes.length === 0 && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <EmptyState
            icon={FlowArrow}
            title="Start with a starter"
            description="Drag a starter from the library onto the canvas, or click one to add it."
          />
        </div>
      )}
    </div>
  );
}
